import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    Send,
    Loader2,
    CheckCircle2,
    AlertCircle,
    ArrowRight,
    ArrowUpRight,
} from "lucide-react";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_bio-guardian/artifacts/slqogudy_Emblem.jpeg";

const FORM_ENDPOINT = "https://formsubmit.co/ajax/founders.yaalee@gmail.com";
const SUBSTACK_URL = "https://yaaleebio.substack.com";

export const Footer = () => {
    const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
    const [status, setStatus] = useState({ state: "idle", msg: "" });
    const [news, setNews] = useState({ email: "", state: "idle" });

    const update = (field) => (e) =>
        setForm((f) => ({ ...f, [field]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (status.state === "loading") return;
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            setStatus({ state: "error", msg: "Please fill name, email, and message." });
            return;
        }
        setStatus({ state: "loading", msg: "" });
        try {
            const res = await fetch(FORM_ENDPOINT, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({
                    name: form.name,
                    phone: form.phone,
                    email: form.email,
                    message: form.message,
                    _subject: `Yaalee · New enquiry from ${form.name}`,
                    _template: "table",
                    _captcha: "false",
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok && (data.success === "true" || data.success === true)) {
                setStatus({
                    state: "success",
                    msg: "Thank you. We've received your message and will write back soon.",
                });
                setForm({ name: "", phone: "", email: "", message: "" });
            } else {
                setStatus({
                    state: "error",
                    msg: data.message || "Something went wrong. Please try again.",
                });
            }
        } catch (err) {
            setStatus({
                state: "error",
                msg: "Network error. Please try again.",
            });
        }
    };

    const handleSubscribe = (e) => {
        e.preventDefault();
        if (!news.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(news.email)) {
            setNews((n) => ({ ...n, state: "error" }));
            return;
        }
        // Open Substack subscribe with email pre-filled
        const url = `${SUBSTACK_URL}/subscribe?email=${encodeURIComponent(
            news.email,
        )}`;
        window.open(url, "_blank", "noopener,noreferrer");
        setNews({ email: "", state: "success" });
    };

    const inputBase =
        "w-full rounded-2xl border border-border bg-background/60 backdrop-blur-sm px-4 py-3 text-[14px] text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition";

    return (
        <footer
            data-testid="site-footer"
            className="relative overflow-hidden border-t border-border"
        >
            <div
                aria-hidden
                className="blob bg-[hsl(var(--brand-moss))]/20"
                style={{ width: 700, height: 700, bottom: -300, right: -200 }}
            />

            <div className="container-x relative py-24 md:py-28">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                    {/* Left — Big elegant statement + CTA + newsletter */}
                    <div className="lg:col-span-7">
                        <p className="overline">
                            <span className="eyebrow-line" />
                            A note from the founders
                        </p>
                        <h2 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-medium leading-[1.02]">
                            Let's build a{" "}
                            <em className="not-italic text-primary">sustainable</em>{" "}
                            future — together.
                        </h2>

                        {/* Discover the Yali story button */}
                        <Link
                            to="/story"
                            data-testid="footer-discover-story"
                            className="group mt-10 inline-flex items-center gap-3 rounded-full bg-primary px-7 py-4 text-sm font-medium text-primary-foreground shadow-md hover:bg-primary/90 transition"
                        >
                            Discover the Yali story
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </Link>

                        {/* Newsletter subscribe */}
                        <div
                            data-testid="footer-newsletter"
                            className="mt-12 max-w-xl rounded-[1.5rem] border border-border bg-card/50 backdrop-blur-sm p-6 md:p-7"
                        >
                            <p className="overline">
                                <span className="eyebrow-line" />
                                Newsletter
                            </p>
                            <h3 className="mt-3 font-display text-2xl md:text-[28px] tracking-tight font-medium leading-tight">
                                Subscribe to our{" "}
                                <em className="not-italic text-primary">Substack</em>.
                            </h3>
                            <p className="mt-2 text-[13.5px] text-muted-foreground leading-relaxed">
                                Field notes on sustainable biotech, rural innovation
                                and the Yaalee mission — straight to your inbox.
                            </p>

                            <form
                                onSubmit={handleSubscribe}
                                className="mt-5 flex flex-col sm:flex-row gap-2.5"
                            >
                                <label htmlFor="news-email" className="sr-only">
                                    Email
                                </label>
                                <input
                                    id="news-email"
                                    data-testid="newsletter-email"
                                    type="email"
                                    placeholder="your@email.com"
                                    value={news.email}
                                    onChange={(e) =>
                                        setNews({
                                            email: e.target.value,
                                            state: "idle",
                                        })
                                    }
                                    className={`${inputBase} flex-1`}
                                    required
                                />
                                <button
                                    type="submit"
                                    data-testid="newsletter-subscribe-btn"
                                    className="group inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-5 py-3 text-sm font-medium text-primary-foreground shadow-md hover:bg-primary/90 transition whitespace-nowrap"
                                >
                                    Subscribe
                                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </button>
                            </form>
                            {news.state === "error" && (
                                <p
                                    data-testid="newsletter-error"
                                    className="mt-3 text-[12px] text-destructive"
                                >
                                    Please enter a valid email address.
                                </p>
                            )}
                            {news.state === "success" && (
                                <p
                                    data-testid="newsletter-success"
                                    className="mt-3 text-[12px] text-primary"
                                >
                                    Completing your subscription in a new tab —
                                    confirm there to finish.
                                </p>
                            )}
                            <p className="mt-3 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                                Powered by Substack · unsubscribe any time
                            </p>
                        </div>
                    </div>

                    {/* Right — Contact form */}
                    <div className="lg:col-span-5">
                        <form
                            data-testid="footer-contact-form"
                            onSubmit={handleSubmit}
                            noValidate
                            className="relative rounded-[1.75rem] border border-border bg-card/60 backdrop-blur-md p-6 md:p-8 shadow-xl shadow-primary/5"
                        >
                            <p className="overline">
                                <span className="eyebrow-line" />
                                Write to us
                            </p>
                            <h3 className="mt-3 font-display text-3xl md:text-4xl tracking-tighter font-medium leading-tight">
                                Start a{" "}
                                <em className="not-italic text-primary">conversation</em>.
                            </h3>

                            <input
                                type="text"
                                name="_honey"
                                style={{ display: "none" }}
                                tabIndex={-1}
                                autoComplete="off"
                            />

                            <div className="mt-6 space-y-3.5">
                                <input
                                    data-testid="cf-name"
                                    type="text"
                                    name="name"
                                    autoComplete="name"
                                    placeholder="Your name *"
                                    value={form.name}
                                    onChange={update("name")}
                                    className={inputBase}
                                    required
                                />
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                                    <input
                                        data-testid="cf-phone"
                                        type="tel"
                                        name="phone"
                                        autoComplete="tel"
                                        placeholder="Phone"
                                        value={form.phone}
                                        onChange={update("phone")}
                                        className={inputBase}
                                    />
                                    <input
                                        data-testid="cf-email"
                                        type="email"
                                        name="email"
                                        autoComplete="email"
                                        placeholder="Email *"
                                        value={form.email}
                                        onChange={update("email")}
                                        className={inputBase}
                                        required
                                    />
                                </div>
                                <textarea
                                    data-testid="cf-message"
                                    name="message"
                                    rows={5}
                                    placeholder="Your message *"
                                    value={form.message}
                                    onChange={update("message")}
                                    className={`${inputBase} resize-none`}
                                    required
                                />
                            </div>

                            {status.state === "success" && (
                                <div
                                    data-testid="cf-success"
                                    className="mt-4 flex items-start gap-2.5 rounded-xl border border-primary/40 bg-primary/10 px-4 py-3 text-[13px] text-foreground"
                                >
                                    <CheckCircle2 className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                                    <span>{status.msg}</span>
                                </div>
                            )}
                            {status.state === "error" && (
                                <div
                                    data-testid="cf-error"
                                    className="mt-4 flex items-start gap-2.5 rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-[13px] text-foreground"
                                >
                                    <AlertCircle className="h-4 w-4 mt-0.5 text-destructive shrink-0" />
                                    <span>{status.msg}</span>
                                </div>
                            )}

                            <button
                                type="submit"
                                data-testid="cf-submit"
                                disabled={status.state === "loading"}
                                className="mt-5 group inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground shadow-md hover:bg-primary/90 transition disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {status.state === "loading" ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Sending…
                                    </>
                                ) : (
                                    <>
                                        Send message
                                        <Send className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                                    </>
                                )}
                            </button>

                            <p className="mt-4 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                                Replies within 1–2 business days
                            </p>
                        </form>
                    </div>
                </div>

                {/* Bottom row */}
                <div className="mt-20 pt-10 border-t border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div className="flex items-center gap-3">
                        <span className="relative h-10 w-10 overflow-hidden rounded-full ring-1 ring-border bg-white">
                            <img
                                src={LOGO_URL}
                                alt="Yaalee Biotech Solutions"
                                className="h-full w-full object-cover scale-110"
                            />
                        </span>
                        <div className="leading-tight">
                            <div className="font-display text-lg font-semibold tracking-tight">
                                Yaalee Biotech Solutions
                            </div>
                            <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mt-0.5">
                                Guardian of sustainable innovation
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-xs text-muted-foreground">
                        <span>© {new Date().getFullYear()} Yaalee Biotech Solutions</span>
                        <span className="hidden md:inline">·</span>
                        <span>Crafted with care, in harmony with nature.</span>
                    </div>
                </div>
            </div>

            <div
                aria-hidden
                className="pointer-events-none select-none w-full overflow-hidden"
            >
                <div className="container-x">
                    <div className="font-display tracking-tighter text-[18vw] leading-none font-medium text-foreground/[0.05] dark:text-foreground/[0.07] -mb-[3vw]">
                        Yaalee
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
